import { FileAPI } from '@business/api/file';
import { FileResponse } from '@business/entities/file';
import { FormId } from '@business/entities/form';
import { OrgId } from '@business/entities/organization';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UserId } from './../../entities/account/UserResponse';
import {
  FileId,
  FolderBreadcrumbResponse,
  FolderId,
  FolderResponse,
  OrgFileShareItem,
  OrgFileShareItemType,
  MediaItemStatus,
} from './../../entities/file/FolderResponse';
import { PresentationEntryFormData } from '@modules/form/AddPresentationModal/AddPresentationModal';

export class FileBloc extends BaseBloc {
  private readonly itemsRepo = new Repository<FileResponse | FolderResponse>({
    getItemId: item => `${item.type}-${item.id}`,
  });

  readonly items$ = this.itemsRepo.items$.pipe(
    map(items => {
      return R.orderBy(items, ['type', 'name'], ['desc', 'asc']);
    }),
  );
  readonly breadcrumb$ = new BehaviorSubject<FolderBreadcrumbResponse[]>([]);

  onReset() {
    this.itemsRepo.reset();
  }

  getItems = (orgId: OrgId, parentId?: FolderId, isPublic?: boolean) => {
    return FileAPI.getItems(orgId, parentId, isPublic).pipe(
      map(data => {
        this.breadcrumb$.next([{ id: '0', name: 'Files' }, ...data.breadcrumbs]);
        return data.items;
      }),
      this.itemsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_files_error') }),
    );
  };

  createFolder = (orgId: OrgId, name: string, parentId?: FolderId, isPublic?: boolean) => {
    return FileAPI.createFolder(orgId, name, parentId, isPublic).pipe(
      this.itemsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_folder'),
      }),
    );
  };

  renameFolder = (id: FolderId, name: string) => {
    return FileAPI.renameFolder(id, name).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('rename_folder'),
      }),
    );
  };

  downloadFolder = (id: FolderId, email: string) => {
    return FileAPI.downloadFolder(id, email).pipe(
      handleMessage({
        type: requestMessage('download_folder'),
      }),
    );
  };

  deleteFolder = (id: FolderId) => {
    return showDeleteConfirmation('Delete Folder', 'Do you really want to remove this folder?').pipe(
      switchMap(() => FileAPI.deleteFolder(id)),
      this.itemsRepo.ops.removeOne(() => `folder-${id}`),
      handleMessage({
        type: requestMessage('delete_folder'),
      }),
    );
  };

  renameFile = (id: FileId, name: string) => {
    return FileAPI.renameFile(id, name).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('rename_file'),
      }),
    );
  };

  deleteFile = (id: FileId) => {
    return showDeleteConfirmation('Delete File', 'Do you really want to remove this file?').pipe(
      switchMap(() => FileAPI.deleteFile(id)),
      this.itemsRepo.ops.removeOne(() => `file-${id}`),
      handleMessage({
        type: requestMessage('delete_file'),
      }),
    );
  };

  createDocument = (orgId: OrgId, name: string, folderId: FolderId) => {
    return FileAPI.createDocument(orgId, name, folderId).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_document'),
      }),
    );
  };

  createForm = (orgId: OrgId, name: string, folderId: FolderId, form: FormId) => {
    return FileAPI.createForm(orgId, name, folderId, form).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_form_file'),
      }),
    );
  };

  saveFormValues = (id: string, data: string) => {
    return FileAPI.saveFormValues(id, data).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('save_form_values'),
      }),
    );
  };

  reloadFile = (id: string) => {
    return FileAPI.reloadFile(id).pipe(this.itemsRepo.ops.upsertOne(item => ({ item })));
  };

  folderStructure = (orgId: OrgId) => {
    return FileAPI.folderStructure(orgId).pipe(
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  move = (id: string, type: 'file' | 'folder', destinationId: string) => {
    return FileAPI.move(id, type, destinationId).pipe(
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  updateOrg = (id: string, type: 'file' | 'folder', orgId: OrgId, folderId?: FolderId) => {
    return FileAPI.updateOrg(id, type, orgId, folderId).pipe(
      this.itemsRepo.ops.removeOne(() => (type === 'folder' ? `folder-${id}` : `file-${id}`)),
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  search = (orgId: OrgId, search: string, parentid?: FolderId) => {
    return FileAPI.search(orgId, search, parentid).pipe(
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  shareForm = (data: { email: string }, id: string) => {
    return FileAPI.shareForm(data, id).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('share_form'),
      }),
    );
  };

  changeFolderVisibility = (id: FolderId) => {
    return FileAPI.FolderVisibility(id).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('folder_visibility'),
      }),
    );
  };

  changeFileVisibility = (id: FileId) => {
    return FileAPI.FileVisibility(id).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('file_visibility'),
      }),
    );
  };

  itemShareUsers = (id: FileId | FolderId, type: string, userIds: UserId[]) => {
    return FileAPI.itemShareUsers(id, type, userIds).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('file_visibility'),
      }),
    );
  };

  shareFile = (id: FileId | FolderId, items: OrgFileShareItem[] = [], type: OrgFileShareItemType = OrgFileShareItemType.file) => {
    return FileAPI.itemPermissions(id, items, type).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_folder_item'),
      }),
    );
  };

  changeFolderStatus = (id: FolderId, status: MediaItemStatus) => {
    return FileAPI.changeFolderStatus(id, status).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_folder_item'),
      }),
    );
  };

  createPresentationEntry = (orgId: string, folderId: string, data: PresentationEntryFormData, moduleId?: string | null, moduleType?: string) => {
    return FileAPI.createPresentationEntry(orgId, folderId, data, moduleId, moduleType).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('fetch_presentations_error'),
      }),
    );
  };
}
